import React from 'react';
import {useRouter} from 'next/router';

import {Button, Link, Text} from '#component/base';
import {Hero as PartialHero} from '#component/partial';
import {sprinkles} from '#style/sprinkles';

import {ActionPlanPage} from '../../ActionPlan.types';

import {HeroContent, HeroProps} from './Hero.types';

const heroContent: Record<ActionPlanPage.State, Omit<HeroContent, 'cta'>> = {
  error: {
    title: 'Action Plan Unavailable',
    description: 'We apologize for the inconvenience. Please contact support to fix this issue.',
  },
  'missing-lab': {
    title: 'Lower your TallyAge™',
    description:
      'Register a lab test to get one step closer to unlocking your personalized Action Plan.',
  },
  'missing-lab-result': {
    title: 'Action Plan in the works',
    description: 'Your TallyAge™ is being processed. Your Action Plan will be available soon.',
  },
  'missing-survey-and-lab': {
    title: 'Action Plan in the works',
    description:
      'While your TallyAge™ is being processed, complete your Lifestyle Survey to get one step closer to unlocking your personalized Action Plan.',
  },
  'missing-survey': {
    title: 'Your new Action Plan',
    description:
      "We're ready to build you a new action plan based on your recent TallyAge™ result. For more accurate recommendations, please take your Lifestyle Survey again to reflect where you are today.",
  },
  locked: {
    title: 'Lower your TallyAge™',
    description:
      'Based on your TallyAge™ result and Lifestyle Survey responses, your Action Plan is a curated list of recommendations designed to help lower your TallyAge™ over time. Available to members only.',
  },
  'non-member': {
    title: 'Lower your TallyAge™',
    description:
      'Based on your TallyAge™ result and Lifestyle Survey responses, your Action Plan is a curated list of recommendations designed to help lower your TallyAge™ over time. Available to members only.',
  },
  inactive: {
    title: 'Activate your Action Plan',
    description: 'Your lifestyle can change, and so can your TallyAge™.',
  },
  'high-performer': {} as HeroContent,
  active: {} as HeroContent,
};

const Hero = ({pageState, onOpenModal}: HeroProps) => {
  const {push} = useRouter();
  const handleBecomeMember = () => {
    push('shop/products/membership');
  };
  const handleContactSupport = () => {
    push('/contact');
  };
  const handleOpenModal = () => {
    onOpenModal();
  };
  const handleLifeStyleClick = () => {
    push('/survey');
  };
  const handleMissingLab = () => {
    push('/register');
  };
  const CTAComponent = () => {
    switch (pageState) {
      case 'error':
        return (
          <Button onClick={handleContactSupport} variant="primary-dark">
            Contact Support
          </Button>
        );
      case 'missing-lab':
        return (
          <Button onClick={handleMissingLab} variant="primary-dark">
            Register Test
          </Button>
        );
      case 'missing-lab-result':
        return <></>;
      case 'missing-survey-and-lab':
      case 'missing-survey':
        return (
          <Button onClick={handleLifeStyleClick} variant="primary-dark">
            Take your Lifestyle Survey
          </Button>
        );
      case 'locked':
      case 'non-member':
        return (
          <Button onClick={handleBecomeMember} variant="primary-dark">
            Become a member
          </Button>
        );
      case 'inactive':
      default:
        return (
          <Text
            as="div"
            color="white"
            variant="buttonSmall"
            className={sprinkles({
              display: 'flex',
              items: 'center',
              gap: 10,
            })}>
            <Link onClick={handleOpenModal} variant="primary-dark">
              How this works
            </Link>
          </Text>
        );
    }
  };

  return (
    <PartialHero>
      <div
        className={sprinkles({
          layout: 'grid',
          items: 'start',
          pT: 80,
          pB: 40,
        })}>
        <Text
          className={sprinkles({colStart: [1, 2], colSpan: 4, w: 'full'})}
          color="white"
          variant="heading1">
          {heroContent[pageState].title}
        </Text>
        <div
          className={sprinkles({
            colStart: [1, 2, 6],
            colSpan: [4, 5],
            mL: [0, 0, 64],
            mT: [0, 0, 18],
          })}>
          <Text color="white" variant="bodyLarge">
            {heroContent[pageState].description}
          </Text>
          <div className={sprinkles({w: 'maxContent', mT: 32})}>
            <CTAComponent />
          </div>
        </div>
      </div>
    </PartialHero>
  );
};

Hero.propTypes = {};

export {Hero};
